import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Home,
  Zap,
  Calendar as CalendarIcon,
  ArrowLeft,
  Eye,
  Timer,
  User,
  ChevronDown,
  X,
  Download
} from 'lucide-react';
import essService from '../../../api/services/essService';
import { toast } from 'react-hot-toast';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import Button from '../../../components/common/Button/Button';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const AttendanceDashboard = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [showAttendanceDetails, setShowAttendanceDetails] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterStatus, setFilterStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Export & Selection State
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Check if any filters are active
  const hasActiveFilters = filterStatus !== 'all' || fromDate !== '' || toDate !== '';

  // Clear all filters
  const handleClearFilters = () => {
    setFilterStatus('all');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [employeeId, currentPage, pageSize, filterStatus, fromDate, toDate]);

  // Attendance data for today (for clock in/out)
  const [attendanceData, setAttendanceData] = useState({
    today: {
      date: new Date().toISOString().split('T')[0],
      status: null,
      hours: 0,
      checkIn: null,
      checkOut: null,
      anomalies: [],
      punches: [],
      sessions: [] // Store all sessions for today
    }
  });

  // Load employee ID from profile
  useEffect(() => {
    const loadEmployeeId = async () => {
      try {
        const profileResult = await essService.getProfile();
        if (profileResult.success && profileResult.data) {
          // Use the string employeeId (e.g., "EMP001") for searching/filtering
          // Fallback to _id search if employeeId string is not present (though it should be)
          setEmployeeId(profileResult.data.employeeId || profileResult.data._id);
        }
      } catch (error) {
      }
    };
    loadEmployeeId();
  }, []);

  // Load attendance data on component mount
  useEffect(() => {
    // loadTodayAttendance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load attendance logs when employee ID, page, or filters change
  useEffect(() => {
    if (employeeId) {
      loadAttendanceLogs();
      loadTodayAttendance(); // Fetch today's real biometric stats
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, currentPage, pageSize, filterStatus, fromDate, toDate]);

  // Load attendance logs from HRMS API (same as employee self-service)
  const loadAttendanceLogs = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", pageSize.toString());
      // The backend expects 'search' for filtering by ID/Name, not 'employeeId' directly
      queryParams.append("search", employeeId); // Use search param to filter by employee ID
      if (filterStatus !== "all") queryParams.append("status", filterStatus);
      if (fromDate) queryParams.append("startDate", fromDate);
      if (toDate) queryParams.append("endDate", toDate);

      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        setAttendanceLogs(data.data.logs || []);
        setPagination(data.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalLogs: 0,
          hasNextPage: false,
          hasPrevPage: false,
          limit: pageSize
        });
      } else {
        throw new Error(data.responseMessage || 'Failed to load attendance logs');
      }
    } catch (error) {
      toast.error('Failed to load attendance logs');
      setAttendanceLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Load real biometric attendance stats for today
  const loadTodayAttendance = async () => {
    if (!employeeId) return;

    try {
      // Fetch today's logs for the employee to calculate stats
      const today = new Date().toISOString().split('T')[0];
      const queryParams = new URLSearchParams();
      // Use search param to filter by employee ID
      queryParams.append("search", employeeId);
      queryParams.append("startDate", today);
      queryParams.append("endDate", today);

      // Use the same biometric-attendance API as AttendanceLogs
      const url = `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance?${queryParams.toString()}`;
      const response = await authFetch(url, {
        credentials: 'include',
        method: "GET",
        headers: {
          "Content-Type": "application/json",
                  },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200 && data.data && data.data.logs && data.data.logs.length > 0) {
          const todayLog = data.data.logs[0];

          // Parse time strings (e.g., "09:30:00")
          const parseTimeMinutes = (timeStr) => {
            if (!timeStr || timeStr === 'N/A') return 0;
            const parts = timeStr.split(':').map(Number);
            if (parts.length < 2) return 0;
            return parts[0] * 60 + parts[1];
          };

          const formatDuration = (minutes) => {
            if (minutes <= 0) return 0;
            return parseFloat((minutes / 60).toFixed(2));
          };

          const checkInMins = parseTimeMinutes(todayLog.checkIn);
          const checkOutMins = parseTimeMinutes(todayLog.checkOut);

          let workingHours = 0;
          if (checkInMins && checkOutMins) {
            let diff = checkOutMins - checkInMins;
            if (diff < 0) diff += 24 * 60;
            workingHours = formatDuration(diff);
          }

          setAttendanceData(prev => ({
            ...prev,
            today: {
              date: today,
              status: todayLog.status || 'Present',
              hours: workingHours,
              checkIn: todayLog.checkIn !== 'N/A' ? todayLog.checkIn : null,
              checkOut: todayLog.checkOut !== 'N/A' ? todayLog.checkOut : null,
              anomalies: [],
              punches: todayLog.checkInPunches || []
            }
          }));
        } else {
          // No logs found for today
          setAttendanceData(prev => ({
            ...prev,
            today: {
              date: today,
              status: 'Absent',
              hours: 0,
              checkIn: null,
              checkOut: null,
              anomalies: [],
              punches: []
            }
          }));
        }
      }
    } catch (error) {
    }
  };

  /* Helper Functions for Time Calculation */
  const parseTimeMinutes = (timeStr) => {
    if (!timeStr || timeStr === 'N/A') return 0;
    const parts = timeStr.split(':').map(Number);
    if (parts.length < 2) return 0;
    return parts[0] * 60 + parts[1];
  };

  const formatDuration = (minutes) => {
    if (minutes <= 0) return "0h 0m";
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const calculateWorkingHours = (checkIn, checkOut) => {
    if (!checkIn || !checkOut || checkIn === 'N/A' || checkOut === 'N/A') return '0h 0m';
    try {
      const startMinutes = parseTimeMinutes(checkIn);
      const endMinutes = parseTimeMinutes(checkOut);
      let diff = endMinutes - startMinutes;
      if (diff < 0) diff += 24 * 60;
      return formatDuration(diff);
    } catch (e) {
      return '0h 0m';
    }
  };

  const format12Hour = (timeStr) => {
    if (!timeStr || timeStr === 'N/A') return 'N/A';
    try {
      const [hours, minutes, seconds] = timeStr.split(':');
      let h = parseInt(hours, 10);
      const m = parseInt(minutes, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12;
      h = h ? h : 12; // the hour '0' should be '12'
      return `${h}:${m < 10 ? '0' + m : m} ${ampm}`;
    } catch (e) {
      return timeStr;
    }
  };


  // Status helper functions (matching employee self-service format)
  const getStatusIcon = (status) => {
    if (!status) return <Clock className="h-4 w-4 text-gray-500" />;
    switch (status.toLowerCase()) {
      case 'present':        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'regularized':    return <CheckCircle className="h-4 w-4 text-teal-500" />;
      case 'late':           return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'half_day':
      case 'half-day':
      case 'half_day_leave': return <AlertCircle className="h-4 w-4 text-amber-500" />;
      case 'short_hours':
      case 'early_left':     return <AlertCircle className="h-4 w-4 text-orange-400" />;
      case 'missed':
      case 'yet_checkout':
      case 'incomplete':     return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'absent':         return <XCircle className="h-4 w-4 text-red-500" />;
      case 'wfh':            return <Home className="h-4 w-4 text-blue-500" />;
      case 'od':             return <MapPin className="h-4 w-4 text-purple-500" />;
      case 'week_off':       return <CalendarIcon className="h-4 w-4 text-blue-400" />;
      case 'holiday':        return <CalendarIcon className="h-4 w-4 text-indigo-500" />;
      case 'on_leave':       return <Clock className="h-4 w-4 text-purple-500" />;
      case 'pending':        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'approved':       return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rejected':       return <XCircle className="h-4 w-4 text-red-500" />;
      default:               return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'text-gray-600 bg-gray-100';
    switch (status.toLowerCase()) {
      case 'present':        return 'bg-green-100 text-green-800';
      case 'regularized':    return 'bg-teal-100 text-teal-800';
      case 'late':           return 'bg-yellow-100 text-yellow-800';
      case 'half_day':
      case 'half-day':       return 'bg-amber-100 text-amber-800';
      case 'half_day_leave': return 'bg-amber-100 text-amber-700';
      case 'short_hours':
      case 'early_left':     return 'bg-orange-100 text-orange-700';
      case 'missed':
      case 'yet_checkout':
      case 'incomplete':     return 'bg-orange-100 text-orange-800';
      case 'absent':         return 'bg-red-100 text-red-800';
      case 'wfh':            return 'bg-blue-100 text-blue-800';
      case 'od':             return 'bg-purple-100 text-purple-800';
      case 'week_off':       return 'bg-blue-100 text-blue-700';
      case 'holiday':        return 'bg-indigo-100 text-indigo-800';
      case 'on_leave':       return 'bg-purple-100 text-purple-800';
      case 'pending':        return 'bg-yellow-100 text-yellow-800';
      case 'approved':       return 'bg-green-100 text-green-800';
      case 'rejected':       return 'bg-red-100 text-red-800';
      default:               return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle view details - fetch full log with sessions (same as employee self-service)
  const handleViewDetails = async (log) => {
    setSelectedAttendanceRecord(log);
    setShowAttendanceDetails(true);

    // Fetch full log details with all sessions
    try {
      const logId = log.logId || log.id;
      // Extract actual log ID if it's a transformed ID
      const actualLogId = logId && typeof logId === 'string' && (logId.includes('_current')
        ? logId.split('_current')[0]
        : logId.includes('_session_')
          ? logId.split('_session_')[0]
          : logId) || logId;
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/biometric-attendance/${actualLogId}`, {
        credentials: 'include',
          method: "GET",
          headers: {
            "Content-Type": "application/json",
                      },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.responseCode === 200) {
          setSelectedAttendanceRecord(data.data); // Update with full log data including sessions
        }
      }
    } catch (error) {
      // Keep the existing log data if fetch fails
    }
  };



  const baseExportFilters = useMemo(() => {
    const f = {};
    if (filterStatus !== 'all') f.status = filterStatus;
    if (fromDate) f.startDate = fromDate;
    if (toDate) f.endDate = toDate;
    return f;
  }, [filterStatus, fromDate, toDate]);

  const currentPageLogIds = useMemo(
    () => attendanceLogs.map((log) => log.id || log.logId).filter(Boolean),
    [attendanceLogs]
  );

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      currentPageIds,
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
    }) => {
      const payload = {
        module: 'my-attendance',
        format,
        scope,
        filters: overrideFilters ?? baseExportFilters,
        page: overridePage ?? currentPage,
        limit: overrideLimit ?? pageSize,
        sortBy: 'date',
        sortOrder: 'desc',
        selectedIds,
      };
      if (Array.isArray(currentPageIds) && currentPageIds.length > 0) {
        payload.currentPageIds = currentPageIds;
      }
      const response = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`, {
        credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
                      },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export request failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackExt = format === 'xlsx' ? 'xlsx' : format;
      const fallbackName = `my-attendance-export.${fallbackExt}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, currentPage, pageSize]
  );

  const handleMyAttendanceSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows);
      if (!ids.length) {
        toast.error('No selected records to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} record(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleMyAttendanceScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        if (scope === 'currentPage' && !attendanceLogs.length) {
          toast.error('No records to export on the current page.');
          return;
        }

        const totalFiltered = Number(pagination.totalLogs ?? 0);
        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No records match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters:
              filterStatus !== 'all' ? { status: filterStatus } : {},
            overridePage: 1,
            overrideLimit: 1000,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageLogIds,
          });
        } else {
          return;
        }

        toast.success('Export downloaded.');
        setShowExportModal(false);
      } catch (error) {
        toast.error(error?.message || 'Export failed. Please try again.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      attendanceLogs.length,
      pagination.totalLogs,
      baseExportFilters,
      filterStatus,
      requestBackendExport,
      currentPageLogIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // --- Export & Selection Logic ---

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Use log.id or log.logId or fallback
      const allIds = attendanceLogs.map(log => log.id || log.logId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    if (!id) return;
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const renderAttendanceCalendar = () => (
    <div className="space-y-6">
      {/* Today's Status - Modern Card Design */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-100 p-8">
        {/* Header Section with Clock In Button */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Today's Attendance</h2>
            <p className="text-sm text-gray-500">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          <div className="flex items-center gap-4">
          </div>
        </div >

        {/* Stats Cards - Modern Design */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">Check In</span>
              <div className="p-2 bg-blue-50 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{attendanceData.today.checkIn || <span className="text-gray-400">--:--:--</span>}</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">Check Out</span>
              <div className="p-2 bg-red-50 rounded-lg">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{attendanceData.today.checkOut || <span className="text-gray-400">--:--:--</span>}</p>
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-600">Total Hours</span>
              <div className="p-2 bg-green-50 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-3xl font-bold text-gray-900">{(attendanceData.today.hours || 0).toFixed(2)}<span className="text-lg text-gray-500">h</span></p>
          </div>
        </div>
      </div >

      {/* Attendance History Table - Using HRMS Logs */}
      < div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8" >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Attendance History</h2>
          <div className="relative shrink-0" ref={selectedExportWrapRef}>
            <Button
              variant="outline"
              icon={
                exportLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )
              }
              onClick={handleExportClick}
              disabled={exportLoading || (attendanceLogs.length === 0 && selectedRows.size === 0)}
              className={`flex items-center space-x-2 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-blue-600' : ''}`}
              aria-label={
                selectedRows.size > 0
                  ? `Export ${selectedRows.size} selected attendance records`
                  : 'Export attendance records'
              }
            >
              <span>
                {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
              </span>
            </Button>
            <SelectedExportFormatBar
              show={selectedRows.size > 0 && showInlineSelectedFormats}
              panelEntered={inlineFormatPanelEntered}
              disabled={exportLoading}
              onSelect={handleMyAttendanceSelectedFormatExport}
            />
          </div>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-sm font-bold text-gray-700 mb-2">Status</label>
            <div className="relative">
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full h-12 pl-4 pr-10 appearance-none bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all shadow-sm font-medium text-gray-700"
              >
                <option value="all">All Status</option>
                <option value="present">Present</option>
                <option value="late">Late</option>
                <option value="half_day">Half Day</option>
                <option value="absent">Absent</option>
              </select>
            </div>
          </div>

          <div className="flex-grow min-w-[300px]">
            <label className="block text-sm font-bold text-gray-700 mb-2">Select Date Range</label>
            <div className="flex items-center gap-2">
              <DateRangeFilter
                startDate={fromDate}
                endDate={toDate}
                onChange={(range) => {
                  setFromDate(range.startDate);
                  setToDate(range.endDate);
                  setCurrentPage(1);
                }}
              />
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearFilters}
                  className="h-10 border-gray-200 text-gray-600 hover:bg-gray-50 transition-all rounded-lg shadow-sm px-4 py-0 text-sm font-semibold"
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </div>

        {
          loading ? (
            <div className="flex flex-col justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4"></div>
              <span className="text-gray-600 font-medium">Loading attendance history...</span>
            </div>
          ) : attendanceLogs.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Calendar className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No attendance records found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 w-10">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                          checked={attendanceLogs.length > 0 && selectedRows.size === attendanceLogs.length}
                          onChange={handleSelectAll}
                          disabled={attendanceLogs.length === 0}
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Check In
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Check Out
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Working Hours
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {attendanceLogs.map((log, index) => {
                      const logDate = new Date(log.date);
                      const dayName = logDate.toLocaleDateString('en-US', { weekday: 'short' });

                      return (
                        <tr
                          key={log.id || log.logId || index}
                          className={`hover:bg-gray-50 ${selectedRows.has(log.id || log.logId) ? 'bg-blue-50/50' : ''}`}
                        >
                          <td className="px-3 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4 cursor-pointer"
                              checked={selectedRows.has(log.id || log.logId)}
                              onChange={() => handleSelectRow(log.id || log.logId)}
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {logDate.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                            <div className="text-xs text-gray-500">{dayName}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {format12Hour(log.checkIn)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {format12Hour(log.checkOut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {calculateWorkingHours(log.checkIn, log.checkOut)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {getStatusIcon(log.status)}
                              <span
                                className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                                  log.status
                                )}`}
                              >
                                {log.status
                                  ? log.status.replace(/_/g, ' ')
                                    .split(' ')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' ')
                                  : 'N/A'}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <button
                              onClick={() => handleViewDetails(log)}
                              className="text-blue-600 hover:text-blue-900 flex items-center space-x-1"
                              aria-label="View attendance details"
                            >
                              <Eye className="h-4 w-4" />
                              <span>Details</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <PaginationControls
                currentPage={pagination.currentPage || 1}
                totalPages={pagination.totalPages || 1}
                totalItems={Number(pagination.totalLogs || attendanceLogs.length || 0)}
                limit={pageSize || 10}
                hasNextPage={pagination.hasNextPage}
                hasPrevPage={pagination.hasPrevPage}
                onPageChange={(page) => {
                  setCurrentPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                onLimitChange={(newLimit) => {
                  setPageSize(newLimit);
                  setCurrentPage(1);
                }}
                limitOptions={[10, 20, 50, 100]}
              />
            </>
          )
        }
      </div >

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Attendance Records"
        showDateRange
        scopedFormatExport={{
          onExport: handleMyAttendanceScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
    </div >
  );

  // Render attendance details view (same as employee self-service)
  const renderAttendanceDetails = () => {
    if (!showAttendanceDetails || !selectedAttendanceRecord) {
      return null;
    }

    const record = selectedAttendanceRecord;
    const recordDate = new Date(record.date);
    const formattedDate = recordDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => {
                      setShowAttendanceDetails(false);
                      setSelectedAttendanceRecord(null);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Back to attendance list"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Attendance Details
                  </h2>
                </div>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Attendance Details */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Attendance Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Date:</span>
                      <span className="text-sm font-medium">
                        {formattedDate}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(record.status)}
                      <span className="text-sm text-gray-600">Status:</span>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${getStatusColor(
                          record.status
                        )}`}
                      >
                        {record.status
                          ? record.status.replace(/_/g, ' ')
                            .split(' ')
                            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Check In:</span>
                      <span className="text-sm font-medium">
                        {format12Hour(record.checkIn)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Check Out:</span>
                      <span className="text-sm font-medium">
                        {format12Hour(record.checkOut)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Timer className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Working Hours:</span>
                      <span className="text-sm font-medium">
                        {calculateWorkingHours(record.checkIn, record.checkOut)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Check In:</span>
                      <span className="text-sm font-medium">
                        {record.checkInPunches ? record.checkInPunches.length : (record.checkIn ? 1 : 0)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Total Check Out:</span>
                      <span className="text-sm font-medium">
                        {record.checkOutPunches ? record.checkOutPunches.length : (record.checkOut ? 1 : 0)}
                      </span>
                    </div>
                    {/* Display Working Hours based on checkIn and checkOut keys directly */}
                    <div className="flex items-center justify-between border-t border-gray-200 pt-2 mt-2">
                      <span className="text-sm font-bold text-gray-700">Gross Working Hours:</span>
                      <span className="text-sm font-bold text-blue-600">
                        {calculateWorkingHours(record.checkIn, record.checkOut)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Punch History Section */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Punch History
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Check In Column */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-100">
                    <div className="flex items-center gap-2 mb-3 border-b border-green-200 pb-2">
                      <div className="p-1 rounded-full bg-green-200">
                        <Clock className="h-4 w-4 text-green-700" />
                      </div>
                      <h4 className="font-semibold text-green-800">Check In</h4>
                    </div>
                    <div className="space-y-2">
                      {record.checkInPunches && record.checkInPunches.length > 0 ? (
                        [...record.checkInPunches].sort().map((time, idx) => (
                          <div key={`in-${idx}`} className="flex items-center justify-between bg-white px-3 py-2 rounded shadow-sm">
                            <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                            <span className="font-mono font-bold text-green-700">{format12Hour(time)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 italic">No check-in records</div>
                      )}
                    </div>
                  </div>

                  {/* Check Out Column */}
                  <div className="bg-red-50 rounded-lg p-4 border border-red-100">
                    <div className="flex items-center gap-2 mb-3 border-b border-red-200 pb-2">
                      <div className="p-1 rounded-full bg-red-200">
                        <Clock className="h-4 w-4 text-red-700" />
                      </div>
                      <h4 className="font-semibold text-red-800">Check Out</h4>
                    </div>
                    <div className="space-y-2">
                      {record.checkOutPunches && record.checkOutPunches.length > 0 ? (
                        [...record.checkOutPunches].sort().map((time, idx) => (
                          <div key={`out-${idx}`} className="flex items-center justify-between bg-white px-3 py-2 rounded shadow-sm">
                            <span className="text-xs font-medium text-gray-500">#{idx + 1}</span>
                            <span className="font-mono font-bold text-red-700">{format12Hour(time)}</span>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 italic">No check-out records</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  // Show details view if active
  if (showAttendanceDetails && selectedAttendanceRecord) {
    return renderAttendanceDetails();
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Back to previous page"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back</span>
              </button>
            )}
            <h1 className="text-2xl font-bold text-gray-900">My Attendance</h1>
          </div>
        </div>

        {/* Main Content - Only Attendance */}
        {renderAttendanceCalendar()}
      </div>
    </div>
  );
};

export default AttendanceDashboard;
